<template>
  <div class="notification-bell" v-clickoutside="closePanel">
    <el-badge :value="unreadCount" :hidden="!unreadCount" :max="99" class="bell-badge">
      <el-button
        type="text"
        :icon="icon"
        class="bell-btn"
        :title="'站内消息'"
        @click="togglePanel"
      />
    </el-badge>

    <transition name="nb-fade">
      <div v-show="panelVisible" class="bell-panel">
        <div class="bell-panel-header">
          <span class="bell-panel-title">
            站内消息
            <span v-if="unreadCount" class="bell-unread-count">（{{ unreadCount }} 条未读）</span>
          </span>
          <el-button
            v-if="unreadCount"
            type="text"
            size="mini"
            class="bell-read-all"
            @click="handleReadAll"
          >全部已读</el-button>
        </div>
        <div v-loading="loading" class="bell-panel-body">
          <div v-if="!list.length && !loading" class="bell-empty">
            <i class="el-icon-chat-line-square" />
            <p>暂无消息</p>
          </div>
          <div
            v-for="item in list"
            :key="item.id"
            class="bell-item"
            :class="{ 'is-read': item.isRead }"
            @click="handleItemClick(item)"
          >
            <div class="bell-item-main">
              <div class="bell-item-row">
                <span class="bell-dot" />
                <span class="bell-item-title">{{ item.title }}</span>
                <span class="bell-item-time">{{ formatTime(item.createdAt) }}</span>
              </div>
              <div v-if="item.content" class="bell-item-content">{{ item.content }}</div>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { getNotifications, getUnreadCount, markRead, markAllRead } from '@/api/notification'

const POLL_INTERVAL = 60 * 1000

export default {
  name: 'NotificationBell',
  props: {
    icon: {
      type: String,
      default: 'el-icon-bell'
    }
  },
  directives: {
    clickoutside: {
      bind(el, binding) {
        el._clickOutsideHandler = (e) => {
          if (el.contains(e.target)) return
          binding.value && binding.value(e)
        }
        document.addEventListener('click', el._clickOutsideHandler)
      },
      unbind(el) {
        document.removeEventListener('click', el._clickOutsideHandler)
        delete el._clickOutsideHandler
      }
    }
  },
  data() {
    return {
      unreadCount: 0,
      panelVisible: false,
      loading: false,
      list: [],
      pollTimer: null
    }
  },
  created() {
    this.loadUnread()
    this.pollTimer = setInterval(this.loadUnread, POLL_INTERVAL)
  },
  beforeDestroy() {
    if (this.pollTimer) clearInterval(this.pollTimer)
  },
  methods: {
    async loadUnread() {
      try {
        const res = await getUnreadCount()
        this.unreadCount = (res.data && res.data.unreadCount) || 0
      } catch (e) {
        // 静默失败
      }
    },
    togglePanel() {
      this.panelVisible = !this.panelVisible
      if (this.panelVisible) this.loadList()
    },
    closePanel() {
      this.panelVisible = false
    },
    async loadList() {
      this.loading = true
      try {
        const res = await getNotifications({ page: 1, pageSize: 10 })
        this.list = (res.data && res.data.list) || []
      } catch (e) {
        this.list = []
      } finally {
        this.loading = false
      }
    },
    // 时间友好化：今天显示 HH:mm，昨天显示「昨天 HH:mm」，今年显示 MM-dd，更早显示 yyyy-MM-dd
    formatTime(str) {
      if (!str) return ''
      const d = new Date(String(str).replace(/-/g, '/'))
      if (isNaN(d.getTime())) return str
      const now = new Date()
      const pad = n => String(n).padStart(2, '0')
      const hm = pad(d.getHours()) + ':' + pad(d.getMinutes())
      const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate())
      if (d >= startOfToday) return hm
      const startOfYesterday = new Date(startOfToday)
      startOfYesterday.setDate(startOfYesterday.getDate() - 1)
      if (d >= startOfYesterday) return '昨天 ' + hm
      if (d.getFullYear() === now.getFullYear()) {
        return pad(d.getMonth() + 1) + '-' + pad(d.getDate()) + ' ' + hm
      }
      return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate())
    },
    async handleItemClick(item) {
      if (!item.isRead) {
        try {
          await markRead(item.id)
          item.isRead = true
          this.loadUnread()
        } catch (e) {
          // 忽略
        }
      }
      if (item.link) {
        this.closePanel()
        this.$router.push(item.link).catch(() => {})
      }
    },
    async handleReadAll() {
      try {
        await markAllRead()
        this.list.forEach(n => { n.isRead = true })
        this.unreadCount = 0
      } catch (e) {
        this.$message.error('操作失败')
      }
    }
  }
}
</script>

<style scoped>
.notification-bell {
  position: relative;
  display: inline-flex;
  align-items: center;
}

.bell-badge {
  line-height: 1;
}

.bell-btn {
  font-size: 18px;
  padding: 4px;
}

.bell-panel {
  position: absolute;
  top: calc(100% + 10px);
  right: 0;
  width: 320px;
  background: #fff;
  border: 1px solid #e9ebef;
  border-radius: 6px;
  box-shadow: 0 3px 14px rgba(0, 0, 0, 0.08);
  z-index: 2000;
  overflow: hidden;
  /* 重置父级继承的行高（如 admin-header/employee-header 的 line-height:50px），
     避免面板内部行高被撑高，保证两端展示一致 */
  line-height: 1.4;
}

.bell-panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 14px;
  border-bottom: 1px solid #f0f1f3;
}

.bell-panel-title {
  font-size: 13px;
  font-weight: 600;
  color: #303133;
}

.bell-unread-count {
  font-size: 12px;
  font-weight: 400;
  color: #909399;
}

.bell-read-all {
  padding: 0;
  font-size: 12px;
}

.bell-panel-body {
  max-height: 320px;
  overflow-y: auto;
}

.bell-empty {
  padding: 36px 0 40px;
  text-align: center;
  color: #d3d7dd;
}

.bell-empty i {
  font-size: 32px;
}

.bell-empty p {
  margin: 6px 0 0;
  font-size: 13px;
  color: #909399;
}

.bell-item {
  padding: 8px 14px;
  cursor: pointer;
  transition: background 0.12s;
  line-height: 1.4;
}

.bell-item + .bell-item {
  border-top: 1px solid #f5f6f8;
}

.bell-item:hover {
  background: #f7f8fa;
}

.bell-item-main {
  min-width: 0;
}

.bell-item-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

/* 未读小圆点：未读红色，已读隐藏占位保持对齐 */
.bell-dot {
  flex-shrink: 0;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #f56c6c;
}

.bell-item.is-read .bell-dot {
  background: transparent;
}

.bell-item-title {
  flex: 1;
  min-width: 0;
  font-size: 13px;
  font-weight: 600;
  color: #303133;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.bell-item.is-read .bell-item-title {
  font-weight: 400;
  color: #6b6f76;
}

.bell-item-time {
  flex-shrink: 0;
  font-size: 12px;
  color: #c0c4cc;
}

.bell-item-content {
  margin: 2px 0 0 14px;
  font-size: 12px;
  color: #909399;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
  word-break: break-all;
}

.nb-fade-enter-active,
.nb-fade-leave-active {
  transition: opacity 0.15s, transform 0.15s;
}

.nb-fade-enter,
.nb-fade-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

@media (max-width: 480px) {
  .bell-panel {
    position: fixed;
    top: 50px;
    left: 10px;
    right: 10px;
    width: auto;
  }
}
</style>
